import { useEffect, Component } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { LanguageProvider, useLanguage, LANGS } from "./context/LanguageContext";
import { AuthProvider } from "./context/AuthContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";
import Home from "./pages/Home";
import About from "./pages/About";
import Fleet from "./pages/Fleet";
import Services from "./pages/Services";
import DriverPage from "./pages/DriverPage";
import Contact from "./pages/Contact";
import Admin from "./pages/Admin";
import PaymentResult from "./pages/PaymentResult";
import AuthPage from "./pages/AuthPage";
import AccountPage from "./pages/AccountPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import BlogPage from "./pages/BlogPage";
import BlogArticlePage from "./pages/BlogArticlePage";
import AuthCallback from "./components/AuthCallback";
import "./App.css";

const PUBLIC_ROUTES = [
  { path: "/", el: <Home /> },
  { path: "/about", el: <About /> },
  { path: "/fleet", el: <Fleet /> },
  { path: "/services", el: <Services /> },
  { path: "/driver", el: <DriverPage /> },
  { path: "/contact", el: <Contact /> },
  { path: "/blog", el: <BlogPage /> },
  { path: "/blog/:slug", el: <BlogArticlePage /> }
];

const PRIVATE_ROUTES = [
  { path: "/auth", el: <AuthPage /> },
  { path: "/account", el: <AccountPage /> },
  { path: "/forgot-password", el: <ForgotPasswordPage /> },
  { path: "/reset-password", el: <ResetPasswordPage /> },
  { path: "/admin", el: <Admin /> },
  { path: "/payment/success", el: <PaymentResult /> },
  { path: "/payment/cancel", el: <PaymentResult cancelled /> }
];

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { err: null };
  }
  static getDerivedStateFromError(e) {
    return { err: e };
  }
  render() {
    if (this.state.err) {
      return (
        <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "#070709", color: "#fff" }}>
          <p>Something went wrong. Please refresh the page.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function LangSync() {
  const location = useLocation();
  const navigate = useNavigate();
  const { lang, setLang } = useLanguage();
  useEffect(() => {
    const seg = location.pathname.split("/")[1];
    if (LANGS.includes(seg)) {
      if (seg !== lang) setLang(seg);
    } else if (/^[a-z]{2}$/.test(seg || "")) {
      navigate("/" + location.pathname.split("/").slice(2).join("/"), { replace: true });
    }
  }, [location.pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  // OAuth callback: process session_id from the URL fragment BEFORE any route renders
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {PUBLIC_ROUTES.map(({ path, el }) => (
        <Route key={path} path={path} element={el} />
      ))}
      {PUBLIC_ROUTES.map(({ path, el }) => (
        <Route key={`lang-${path}`} path={`/:langPrefix${path === "/" ? "" : path}`} element={el} />
      ))}
      {PRIVATE_ROUTES.map(({ path, el }) => (
        <Route key={path} path={path} element={el} />
      ))}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.08, smoothWheel: true, wheelMultiplier: 1.05 });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <ErrorBoundary>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <LangSync />
            <div className="relative min-h-screen bg-obsidian text-white overflow-x-clip">
              <Navbar />
              <AppRouter />
              <Footer />
              <WhatsAppButton />
            </div>
          </BrowserRouter>
        </AuthProvider>
        <Toaster theme="dark" position="top-center" richColors />
      </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
